import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Instagram, Facebook, Twitter, MessageCircle, Copy, Download, RefreshCw, FileText, Lock } from 'lucide-react';
import { ShareableResultCard } from './ShareableResultCard';
import { Pattern } from '../../types/scan';
import { trackResultShared } from '../../utils/analytics';
type AspectRatio = 'square' | 'story';
interface SocialShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareAspectRatio: AspectRatio;
  onAspectRatioChange: (ratio: AspectRatio) => void;
  onSocialShare: (platform: 'instagram' | 'facebook' | 'tiktok' | 'twitter' | 'youtube' | 'whatsapp') => void;
  onDownloadImage: () => void;
  onDownloadPDF: () => void;
  isGeneratingImage: boolean;
  isGeneratingPDF: boolean;
  score: number;
  riskLevel: 'low' | 'medium' | 'high';
  messageCount: number | null;
  patterns: any[];
  /** Free results: the PDF button routes to unlock instead of downloading. */
  pdfLocked?: boolean;
  summary?: string | null;
  shareCardTheme: 'dark' | 'light';
  onThemeChange: (theme: 'dark' | 'light') => void;
  /** Optional copy overrides, e.g. for the first-scan share prompt. */
  eyebrow?: string;
  title?: string;
  subtitle?: string;
}
export function SocialShareModal({
  isOpen,
  onClose,
  shareAspectRatio,
  onAspectRatioChange,
  onSocialShare,
  onDownloadImage,
  onDownloadPDF,
  isGeneratingImage,
  isGeneratingPDF,
  score,
  riskLevel,
  messageCount,
  patterns,
  summary,
  shareCardTheme,
  onThemeChange,
  pdfLocked = false,
  eyebrow,
  title = 'Share Analysis',
  subtitle = 'Export your forensic report or share it directly to social media.'
}: SocialShareModalProps) {
  if (!isOpen) return null;
  // Calculate scaling for the preview
  // The original cards are 1080px wide. We want to fit them in a ~300px container.
  const previewScale = 300 / 1080;
  const previewHeight = shareAspectRatio === 'story' ? 1920 * previewScale : 1080 * previewScale;
  return <AnimatePresence data-id="element-609" data-mp="42qp34ujbjy6">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/95 backdrop-blur-md overflow-y-auto" data-id="element-610" data-mp="441t95rw6tpt">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="w-full max-w-5xl bg-[#0A0A0A] border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row relative my-auto" data-id="element-611" data-mp="3qha2ay7rwt32">
          {/* Close Button */}
          <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-white/10 rounded-full text-white/70 hover:text-white transition-colors backdrop-blur-sm" data-id="element-612" data-mp="2yqngloihv1n2">
            <X size={20} data-id="element-613" data-mp="19atam7m8jqht" />
          </button>

          {/* Left Side: Live Preview */}
          <div className="w-full md:w-1/2 bg-[#050505] border-r border-white/5 p-8 flex flex-col items-center justify-center relative overflow-hidden" data-id="element-614" data-mp="1e94k2h8mbyzi">
            {/* Subtle background grid */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-615" data-mp="1n1ok9m4k6bmg" />

            <div className="relative z-10 flex flex-col items-center w-full" data-id="element-616" data-mp="1wx9lhi1sz7v5">
              <h3 className="text-white/80 font-medium mb-6 uppercase tracking-widest text-sm" data-id="element-617" data-mp="21yysmmlwn1dy">
                Preview
              </h3>

              {/* Preview Container */}
              <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10 transition-all duration-500 ease-in-out" style={{
              width: 300,
              height: previewHeight
            }} data-id="element-618" data-mp="7keih3v7mo8t">
                <div className="absolute top-0 left-0 origin-top-left transition-all duration-500 ease-in-out" style={{
                transform: `scale(${previewScale})`,
                width: 1080,
                height: shareAspectRatio === 'story' ? 1920 : 1080
              }} data-id="element-619" data-mp="38fn9tjdlpmn0">
                  <ShareableResultCard score={score} riskLevel={riskLevel} messageCount={messageCount} topPatterns={patterns.slice(0, shareAspectRatio === 'story' ? 4 : 3)} variant={shareAspectRatio === 'story' ? 'vertical' : 'square'} summary={summary || undefined} theme={shareCardTheme} data-id="element-620" data-mp="2to4y3xrrye9g" />
                </div>
              </div>
            </div>
          </div>

          {/* Right Side: Controls */}
          <div className="w-full md:w-1/2 p-8 flex flex-col" data-id="element-621" data-mp="qa2ue6qnnxrz">
            {eyebrow && <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF0000]" data-id="element-622" data-mp="cj7jyq0id8ma">
                {eyebrow}
              </p>}
            <h2 className="text-2xl font-bold text-white mb-2 pr-10" data-id="element-623" data-mp="149c3kmgg1rmg">
              {title}
            </h2>
            <p className="text-white/50 text-sm mb-8" data-id="element-624" data-mp="198ovhlm69ovw">{subtitle}</p>

            {/* Format Toggle */}
            <div className="mb-8" data-id="element-625" data-mp="v975j2x545ky">
              <label className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3 block" data-id="element-626" data-mp="v84fex7n6763">
                Format
              </label>
              <div className="flex bg-white/5 p-1 rounded-xl border border-white/10" data-id="element-627" data-mp="3fywlxkdhrzfr">
                <button onClick={() => onAspectRatioChange('story')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${shareAspectRatio === 'story' ? 'bg-white/10 text-white shadow-sm' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`} data-id="element-628" data-mp="3kxpynakyfmev">
                  <div className="w-3 h-4 border-2 border-current rounded-[2px]" data-id="element-629" data-mp="1gpv9sgeo549d" />
                  Story (9:16)
                </button>
                <button onClick={() => onAspectRatioChange('square')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${shareAspectRatio === 'square' ? 'bg-white/10 text-white shadow-sm' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`} data-id="element-630" data-mp="17qqbnb70rcxq">
                  <div className="w-4 h-4 border-2 border-current rounded-[2px]" data-id="element-631" data-mp="ttbhjt5tmgzf" />
                  Square (1:1)
                </button>
              </div>
            </div>

            {/* Theme Toggle */}
            <div className="mb-8" data-id="element-632" data-mp="2o3f4odh4hepn">
              <label className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3 block" data-id="element-633" data-mp="9y0glyqu06br">
                Card Style
              </label>
              <div className="flex bg-white/5 p-1 rounded-xl border border-white/10" data-id="element-634" data-mp="euqrm7ns4bqb">
                <button onClick={() => onThemeChange('dark')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${shareCardTheme === 'dark' ? 'bg-white/10 text-white shadow-sm' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`} data-id="element-635" data-mp="jntq8u7eybdd">
                  <div className="w-4 h-4 rounded-full bg-[#0A0A0A] border border-white/20" data-id="element-636" data-mp="3w472c86o6ieg" />
                  Dark
                </button>
                <button onClick={() => onThemeChange('light')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${shareCardTheme === 'light' ? 'bg-white/10 text-white shadow-sm' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`} data-id="element-637" data-mp="4zywdaho60yp">
                  <div className="w-4 h-4 rounded-full bg-white border border-slate-200" data-id="element-638" data-mp="n2fpyp22g40h" />
                  Light
                </button>
              </div>
            </div>

            {/* Social Grid */}
            <div className="mb-8" data-id="element-639" data-mp="w2c7m6q1g2m1">
              <label className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3 block" data-id="element-640" data-mp="w1awy3cxpi4u">
                Share To
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-641" data-mp="15sx51qtz713d">
                <ShareButton icon={<Instagram size={20} data-id="element-643" data-mp="1jsgceheyq8em" />} label="Instagram" note="Copies caption" onClick={() => onSocialShare('instagram')} className="hover:bg-gradient-to-br hover:from-purple-600/20 hover:to-pink-600/20 hover:border-pink-500/50 hover:text-pink-400" data-id="element-642" data-mp="15urpz558yt7g" />
                <ShareButton icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" data-id="element-645" data-mp="37drge4grjell">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" data-id="element-646" data-mp="2xoztiaqk7khj" />
                    </svg>} label="TikTok" note="Copies caption" onClick={() => onSocialShare('tiktok')} className="hover:bg-white/10 hover:border-white/30 hover:text-white" data-id="element-644" data-mp="3hjrcwpjt98rp" />
                <ShareButton icon={<Facebook size={20} data-id="element-648" data-mp="cxphs1g2k3nt" />} label="Facebook" onClick={() => onSocialShare('facebook')} className="hover:bg-blue-600/10 hover:border-blue-500/50 hover:text-blue-400" data-id="element-647" data-mp="cxzcu3fz110d" />
                <ShareButton icon={<Twitter size={20} data-id="element-650" data-mp="1l5evhozf6tmc" />} label="X / Twitter" onClick={() => onSocialShare('twitter')} className="hover:bg-white/10 hover:border-white/30 hover:text-white" data-id="element-649" data-mp="1gc29lmm4l0xa" />
                <ShareButton icon={<MessageCircle size={20} data-id="element-652" data-mp="1z4mv2izhw3r2" />} label="WhatsApp" onClick={() => onSocialShare('whatsapp')} className="hover:bg-green-500/10 hover:border-green-500/50 hover:text-green-400" data-id="element-651" data-mp="1bflgee02jwdc" />
                <ShareButton icon={<Copy size={20} data-id="element-654" data-mp="1q6azs3noohsa" />} label="Copy Link" onClick={async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  // Fired only after the copy actually succeeded. The URL
                  // itself is never sent — it carries a scan id.
                  trackResultShared('copy_link', 'scan_result');
                } catch {
                  // Clipboard denied — nothing to report.
                }
                // Toast is handled in AnalysisPage
              }} className="hover:bg-white/10 hover:border-white/30 hover:text-white" data-id="element-653" data-mp="1q3gbu25goeu4" />
              </div>
            </div>

            <div className="mt-auto space-y-3" data-id="element-655" data-mp="3om1n11405pik">
              <button onClick={onDownloadImage} disabled={isGeneratingImage} className="w-full py-3.5 bg-white text-black rounded-xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-656" data-mp="3jn76k5ticd1m">
                {isGeneratingImage ? <>
                    <RefreshCw size={18} className="animate-spin" data-id="element-657" data-mp="30wh0674qa840" />
                    Generating Image...
                  </> : <>
                    <Download size={18} data-id="element-658" data-mp="14r8aoq6ehn1m" />
                    Download Image
                  </>}
              </button>

              <button onClick={onDownloadPDF} disabled={isGeneratingPDF} className="w-full py-3.5 bg-transparent border border-white/20 text-white rounded-xl font-bold hover:bg-white/5 hover:border-white/40 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-659" data-mp="1eicgp0e3lxkb">
                {pdfLocked ? <>
                    <Lock size={18} data-id="element-660" data-mp="utbqz9zedjrw" />
                    Unlock PDF Report
                  </> : isGeneratingPDF ? <>
                    <RefreshCw size={18} className="animate-spin" data-id="element-661" data-mp="26ajvdva1mzw2" />
                    Generating PDF...
                  </> : <>
                    <FileText size={18} data-id="element-662" data-mp="2bay4lgndsfvo" />
                    Download PDF Case File
                  </>}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}
function ShareButton({
  icon,
  label,
  note,
  onClick,
  className
}: {
  icon: React.ReactNode;
  label: string;
  note?: string;
  onClick: () => void;
  className: string;
}) {
  return <button onClick={onClick} className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-white/10 bg-white/5 transition-all text-white/70 ${className}`} data-id="element-663" data-mp="22n3kop1i6f5l">
      <div className="mb-1" data-id="element-664" data-mp="1snutu51xpgng">{icon}</div>
      <span className="text-xs font-bold" data-id="element-665" data-mp="1sowen18r5hid">{label}</span>
      {note && <span className="text-[9px] text-white/40 font-medium" data-id="element-666" data-mp="1onuemi70h1fc">{note}</span>}
    </button>;
}